import { EnumCreditAllocationStatus } from '@wallet-manager/pfh-pmp-node-def-types/dist/src/DbModel/SwapAgent';
import { ApiResult } from 'src/api/types';
import getDisplayApprovalProgress from 'src/common/approval/helpers/getDisplayApprovalProgress';
import { useListMappingTransform, useTranslation } from 'src/hooks';
import { GetListRes } from '../types/GetListRes';
import { TableColumnKey, TableRow } from '../types/Table';
import { displayAmountCurrying, toDisplayTime } from 'src/utils';
import { getTransferRequestStatusDisplayValue } from 'src/helper/displayValue/getTransferRequestStatusDisplayValue';

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type MapResultToTupleArray = (row: GetListRes[], omitKeys: string[]) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { apiList } = props;

  const { tc, translate } = useTranslation();

  const listMappingTransform = useListMappingTransform('table');

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const toDisplayAmount = displayAmountCurrying(item.creditCurrencyDecimals ?? 0);
      const creditAmountDisplayValue = toDisplayAmount(item.creditAmount ?? 0);

      const approvalProgress = getDisplayApprovalProgress({
        approvalsCount: item.approvalsCount ?? 0,
        approvalsRequired: item.approvalsRequired ?? 0,
        applicationStatus: item.status ?? 0,
        approvedStatus: EnumCreditAllocationStatus.Approved,
        translateFunc: tc,
      });

      const statusDisplayValue = getTransferRequestStatusDisplayValue(item.status ?? 0, translate);
      const mappedResult = [
        [TableColumnKey.OrderId, item.targetTransferOrderId],
        [TableColumnKey.ApprovalProgress, approvalProgress],
        [TableColumnKey.PfhNo, item.toWalletClientId],
        [TableColumnKey.CreditCurrency, item.creditCurrency],
        [TableColumnKey.CreditAmount, creditAmountDisplayValue],
        [TableColumnKey.Status, statusDisplayValue],
        [TableColumnKey.CreatedBy, item.createdBy],
        [TableColumnKey.CreationTime, toDisplayTime(item.createdDate ?? null)],
        [TableColumnKey.LastModifiedTime, toDisplayTime(item.lastModifiedDate ?? null)],
        ['rawData', item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform);

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTableRows;
